/**
 * Format transaction type for display
 * Maps backend enum values to user-friendly labels with icons
 */
export const formatTransactionType = (type) => {
    const typeMap = {
        COIN_PURCHASE: { label: 'Coin Purchase' },
        CHAPTER_PURCHASE: { label: 'Chapter Purchase' },
        CHAPTER_SALE: { label: 'Chapter Sale' },
        REFUND: { label: 'Refund' },
        REWARD: { label: 'Reward' },
        INCOMING_TRANSFER: { label: 'Incoming Transfer' },
        OUTGOING_TRANSFER: { label: 'Outgoing Transfer' },
        CONVERSION: { label: 'Conversion', },
        WITHDRAWAL_REQUEST: { label: 'Withdrawal' },
        // fallback for mock data
        'Added funds': { label: 'Added Funds' },
        'Funds sent': { label: 'Funds Sent' },
        'BD coins sent': { label: 'BD Coins Sent' },
    };

    return typeMap[type] || { label: type || 'Unknown', };
};

/**
 * Format amount with currency or coin indicator
 */
export const formatAmount = (amount, isCoin = false) => {
    if (isCoin) {
        return `${amount}`;
    }
    // format as currency
    return typeof amount === 'number' ? `$${amount.toFixed(2)}` : `$${amount}`;
};

/**
 * Format date to readable format
 */
export const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        // Show relative time for recent transactions
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;

        // Show full date format
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch (e) {
        return dateString;
    }
};

/**
 * Format payment method
 */
export const formatPaymentMethod = (method) => {
    const methodMap = {
        STRIPE: 'Stripe',
        PAYPAL: 'PayPal',
        GOOGLE_PAY: 'Google Pay',
        GOOGLE_PLAY: 'Google Pay',
        APPLE_PAY: 'Apple Pay',
        CARD: 'Credit Card',
        BANK_TRANSFER: 'Bank Transfer',
        BD_TRANSFER: 'BD Transfer',
    };

    return methodMap[method] || method || 'N/A';
};

/**
 * Resolve the amount to display for a transaction. COIN_PURCHASE/REFUND rows
 * carry both a coin count (coinAmount) and the price paid (dollarAmount) -
 * the plain `amount` field on those rows is the dollar price, not a coin
 * count, so it must not be shown with the coin icon.
 */
export const getTransactionAmountInfo = (tx) => {
    const hasCoinSplit = tx?.coinAmount != null && tx?.dollarAmount != null;
    const isCoin = hasCoinSplit ? true : !["CONVERSION", "WITHDRAWAL_REQUEST"].includes(tx?.type);
    const value = hasCoinSplit ? tx.coinAmount : tx?.amount;
    const isRefund = tx?.type === 'REFUND';

    return {
        isCoin,
        display: formatAmount(value, isCoin),
        primaryLabel: hasCoinSplit ? (isRefund ? 'Coins Refunded' : 'Coins Purchased') : 'Amount',
        paidDisplay: hasCoinSplit ? formatAmount(tx.dollarAmount, false) : null,
        paidLabel: isRefund ? 'Amount Refunded' : 'Amount Paid',
    };
};

/**
 * Get transaction direction for UI (inflow vs outflow)
 */
export const isMoneyInflow = (type) => {
    const inflowTypes = ['COIN_PURCHASE', "INCOMING_TRANSFER", 'REWARD', 'REFUND', 'CHAPTER_SALE'];
    return inflowTypes.includes(type);
};

/**
 * Get status color
 */
export const getStatusColor = (status) => {
    const colorMap = {
        PENDING: 'text-yellow-500',
        SUCCESSFUL: 'text-green-500',
        SUCCESS: 'text-green-500',
        FAILED: 'text-red-500',
        CANCELLED: 'text-gray-400',
    };

    return colorMap[status?.toUpperCase()] || 'text-gray-400';
};

/**
 * Get transaction title based on type and recipient
 */
export const getTransactionTitle = (transaction) => {
    const { type, recipientName, senderName } = transaction;
    const typeInfo = formatTransactionType(type);

    if (type === 'TRANSFER' && recipientName) {
        return `${typeInfo.label} to ${recipientName}`;
    }
    if (senderName && !isMoneyInflow(type)) {
        return `${typeInfo.label} from ${senderName}`;
    }

    return typeInfo.label;
};
